import { ReactNode, useEffect, useRef, useState } from 'react';
import styles from './SideDrawer.module.css';
import { ScenarioCharacter } from '../../api/types/scenario';
import { GrUserAdd } from "react-icons/gr";
import { IoArrowBack, IoChevronBack, IoClose, IoSearch } from "react-icons/io5";
import { api } from '../../api';
import { CharacterListResponse } from '../../api/types/chat';
import WorldCharacterCard from './WorldCharacterCard';
import { filterAvailableCharacters } from '../../utils/characterUtils';

interface SideDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  children?: ReactNode;
  title?: string;
  characters?: ScenarioCharacter[];
  availableCharacters?: ScenarioCharacter[];
  onAddAvailableCharacter?: (character: CharacterListResponse) => void;
  onRemoveAvailableCharacter?: (characterId: string) => void;
  scenarioId?: string;
}

// Custom double chevron component
const DoubleChevronBack = ({ size = 24 }) => (
  <div className={styles.doubleChevron}>
    <IoChevronBack size={size} />
    <IoChevronBack size={size} style={{ marginLeft: '-12px' }} />
  </div>
);

function SideDrawer({ isOpen, onClose, children, title = "World Characters", characters = [], availableCharacters = [], onAddAvailableCharacter, onRemoveAvailableCharacter, scenarioId }: SideDrawerProps): JSX.Element {
  const [isClosing, setIsClosing] = useState<boolean>(false);
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [isButtonPressed, setIsButtonPressed] = useState<boolean>(false);
  const [showSearch, setShowSearch] = useState<boolean>(false);
  const [isAddMode, setIsAddMode] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [searchResults, setSearchResults] = useState<CharacterListResponse[]>([]);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [isAddingCharacter, setIsAddingCharacter] = useState<boolean>(false);
  const [selectedCharacter, setSelectedCharacter] = useState<CharacterListResponse | null>(null);
  const [isTransitioning, setIsTransitioning] = useState<boolean>(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const initialLoadRef = useRef<boolean>(false);
  const prevSearchQueryRef = useRef<string>('');
  const [newlyAddedCharacterId, setNewlyAddedCharacterId] = useState<string | null>(null);
  const [removingCharacterId, setRemovingCharacterId] = useState<string | null>(null);
  const [loadingCharacterIds, setLoadingCharacterIds] = useState<string[]>([]);

  // Handle open/close animations
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      setIsClosing(false);
    } else if (isVisible) {
      // Start closing animation
      setIsClosing(true);
      // Wait for animation to complete before removing from DOM
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, 300); // Match this with the CSS animation duration
      return () => clearTimeout(timer);
    }
  }, [isOpen, isVisible]);

  // Prevent body scrolling when drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Close drawer on escape key
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      // Add event listener for Escape key
      const handleEscKey = (event: globalThis.KeyboardEvent) => {
        if (event.key === "Escape") {
          handleClose();
        }
      };
      
      window.addEventListener("keydown", handleEscKey);
      return () => {
        window.removeEventListener("keydown", handleEscKey);
      };
    }
  }, [isOpen]);

  // Search for characters when the search query changes
  useEffect(() => {
    if (!isAddMode) return;
    
    // Skip if the search query hasn't actually changed
    if (prevSearchQueryRef.current === searchQuery) return;
    prevSearchQueryRef.current = searchQuery;
    
    // Clear any existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    const searchCharacters = async () => {
      if (searchQuery.trim().length === 0) {
        setIsSearching(true);
        await loadTrendingCharacters();
        return;
      }
      
      setIsSearching(true);
      try {
        const results = await api.characters.search(searchQuery);
        // Filter out characters that are already in the scenario or in availableCharacters
        const filteredResults = filterAvailableCharacters(results, characters, availableCharacters);
        setSearchResults(filteredResults);
      } catch (error) {
        console.error('Error searching for characters:', error);
      } finally {
        setIsSearching(false);
      }
    };

    // Execute search immediately without debounce for better responsiveness
    searchCharacters();
    
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery, isAddMode, characters, availableCharacters]);

  // Load trending characters when entering add mode
  useEffect(() => {
    if (isAddMode && !initialLoadRef.current) {
      initialLoadRef.current = true;
      setIsSearching(true);
      loadTrendingCharacters();
    }
    
    // Reset the initial load flag when exiting add mode
    if (!isAddMode) {
      initialLoadRef.current = false;
    }
  }, [isAddMode]);

  const loadTrendingCharacters = async () => {
    try {
      const trending = await api.characters.filter("trending");
      // Filter out characters that are already in the scenario or in availableCharacters
      const filteredTrending = filterAvailableCharacters(trending, characters, availableCharacters);
      setSearchResults(filteredTrending);
    } catch (error) {
      console.error('Error loading trending characters:', error);
    } finally {
      setIsSearching(false);
    }
  };

  const handleClose = () => {
    setIsClosing(true);
    // Wait for animation to complete before calling onClose
    setTimeout(() => {
      onClose();
    }, 300); // Match this with the CSS animation duration
  };

  const handleCloseButtonClick = () => {
    setIsButtonPressed(true);
    setTimeout(() => {
      setIsButtonPressed(false);
      handleClose();
    }, 150);
  };

  const handleAddCharacterMode = () => {
    setIsAddMode(true);
    setShowSearch(true);
    initialLoadRef.current = false; // Reset the initial load flag
  };

  const handleBackToList = () => {
    setIsAddMode(false);
    setShowSearch(false);
    setSearchQuery('');
    setSearchResults([]);
    initialLoadRef.current = false; // Reset the initial load flag
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleSelectCharacter = (character: CharacterListResponse) => {
    setSelectedCharacter(character);
    setIsAddingCharacter(true);
    setIsTransitioning(true);
    
    // Add character ID to loading state
    setLoadingCharacterIds(prev => [...prev, character.id]);
    
    // Show a brief animation before transitioning back
    setTimeout(() => {
      if (onAddAvailableCharacter) {
        onAddAvailableCharacter(character);
      }
      
      // Set a notification for successful addition
      setNotification({ 
        message: `${character.display_name} added to your world!`, 
        type: 'success' 
      });
      setTimeout(() => setNotification(null), 3000);
      
      // Transition back to character list with animation
      setIsAddMode(false);
      
      // Reset states after animation completes
      setTimeout(() => {
        setShowSearch(false);
        setSearchQuery('');
        setSearchResults([]);
        setIsAddingCharacter(false);
        setSelectedCharacter(null);
        setIsTransitioning(false);
        // Remove from loading state
        setLoadingCharacterIds(prev => prev.filter(id => id !== character.id));
      }, 300); // Match this with the CSS animation duration
    }, 400); // Time to show the selection animation
  };

  const handleRemoveCharacter = (characterId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent event bubbling
    
    // Add character ID to loading state
    setLoadingCharacterIds(prev => [...prev, characterId]);
    
    if (onRemoveAvailableCharacter) {
      onRemoveAvailableCharacter(characterId);
    }
    
    // Remove from loading state after a short delay
    setTimeout(() => {
      setLoadingCharacterIds(prev => prev.filter(id => id !== characterId));
    }, 300);
  };

  // Add a new function to handle adding a character to the scenario
  const handleAddCharacterToScenario = async (characterId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent event bubbling
    
    if (!scenarioId) {
      setNotification({ message: 'Cannot add character: scenario ID is missing', type: 'error' });
      setTimeout(() => setNotification(null), 3000);
      return;
    }
    
    // Add character ID to loading state
    setLoadingCharacterIds(prev => [...prev, characterId]);
    
    try {
      // Find the character to be added
      const character = availableCharacters.find(c => c.id === characterId);
      if (!character) {
        setLoadingCharacterIds(prev => prev.filter(id => id !== characterId));
        return;
      }
      
      // Get the scenario and add the character
      const scenario = await api.scenarios.get(scenarioId);
      await scenario.addCharacter(characterId);
      
      // Show success notification
      const characterName = character.display_name;
      setNotification({ message: `${characterName} added to scenario successfully!`, type: 'success' });
      setTimeout(() => setNotification(null), 3000);
      
      // Remove the character from available characters
      if (onRemoveAvailableCharacter) {
        onRemoveAvailableCharacter(characterId);
      }
    } catch (error) {
      console.error('Failed to add character to scenario:', error);
      setNotification({ message: 'Failed to add character to scenario', type: 'error' });
      setTimeout(() => setNotification(null), 3000);
    } finally {
      // Remove from loading state
      setLoadingCharacterIds(prev => prev.filter(id => id !== characterId));
    }
  };

  // Convert CharacterListResponse to ScenarioCharacter format for display
  const convertToScenarioCharacter = (character: CharacterListResponse): ScenarioCharacter => {
    return {
      id: character.id,
      username: character.username,
      display_name: character.display_name,
      tagline: character.tagline,
      icon: character.icon,
      is_mature: false // Default value since it's not present in CharacterListResponse
    };
  };

  if (!isVisible && !isOpen) return <></>;

  return (
    <>
      {/* Backdrop overlay */}
      <div 
        className={`${styles.backdrop} ${isClosing ? styles.backdropExit : ''}`} 
        onClick={handleClose}
        aria-hidden="true"
      />
      
      {/* Drawer content */}
      <div
        className={`${styles.drawer} ${isClosing ? styles.drawerExit : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className={`${styles.closeButton} ${isButtonPressed ? styles.closeButtonPressed : ""}`}
          onClick={handleCloseButtonClick}
          onMouseDown={() => setIsButtonPressed(true)}
          onMouseUp={() => setIsButtonPressed(false)}
          onMouseLeave={() => isButtonPressed && setIsButtonPressed(false)}
          aria-label="Close"
        >
          <DoubleChevronBack size={20} />
        </button>
        
        {/* Notification */}
        {notification && (
          <div className={`${styles.notification} ${styles[notification.type]}`}>
            {notification.message}
          </div>
        )}
        
        <div className={styles.content}>
          {title && (
            <div className={styles.titleContainer}>
              {isAddMode ? (
                <>
                  <button 
                    className={styles.backButton}
                    onClick={handleBackToList}
                    aria-label="Back to character list"
                    disabled={isAddingCharacter}
                  >
                    <IoArrowBack size={20} />
                  </button>
                  <h2 className={`${styles.title} ${styles.smallerTitle}`}>Add Character to Your World</h2>
                </>
              ) : (
                <h2 className={styles.title}>{title}</h2>
              )}
            </div>
          )}
          
          <div className={styles.addCharacterSection}>
            {(showSearch || isAddMode) && (
              <div className={`${styles.searchBarContainer} ${styles.searchBarVisible}`}>
                <input
                  type="text"
                  className={styles.searchInput}
                  placeholder="Search characters..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  autoFocus
                  disabled={isAddingCharacter}
                />
                <button className={styles.searchButton} disabled={isAddingCharacter}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            )}
          </div>
          
          {/* Character List - only show when not in add mode */}
          {!isAddMode && (
            <div className={`${styles.characterList} ${isTransitioning ? styles.fadeIn : ''}`}>
              {/* Add Character Button at the top */}
              <button 
                className={styles.bigPlusButton}
                onClick={handleAddCharacterMode}
                aria-label="Add character"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              
              {availableCharacters.map((character) => (
                <div 
                  key={character.id} 
                  className={`${styles.characterItem} ${newlyAddedCharacterId === character.id ? styles.newlyAdded : ''} ${removingCharacterId === character.id ? styles.removing : ''}`}
                >
                  <div className={styles.characterAvatar}>
                    <img src={character.icon} alt={character.display_name} />
                  </div>
                  <div className={styles.characterInfo}>
                    <div className={styles.characterName}>{character.display_name}</div>
                    <div className={styles.characterDescription}>
                      {/* This would be a description if available in the character object */}
                      {character.display_name === "Sirius Black" && "good looking and carelessly handsome"}
                      {character.display_name === "Peter Pettigrew" && "Peter gives you his jacket."}
                      {character.display_name === "Remus Lupin" && "smoking up in the astronomy tower"}
                      {character.display_name === "James Potter" && "As the golden child of the Potter family, James'..."}
                      {character.display_name === "Lily Evans" && "A skilled potioneer"}
                      {character.display_name === "Young Severus Snape" && "The sad boy from Spinner's End."}
                      {character.display_name === "Regulus Black" && "you've never spoken to him but you know enough..."}
                    </div>
                  </div>
                  <div className={styles.characterActions}>
                    {loadingCharacterIds.includes(character.id) ? (
                      <div className={styles.characterActionLoading}>
                        <div className={styles.smallLoadingSpinner}></div>
                      </div>
                    ) : (
                      <>
                        <button 
                          className={styles.characterRemoveButton}
                          onClick={(e) => handleRemoveCharacter(character.id, e)}
                          aria-label="Remove character"
                        >
                          <IoClose size={16} />
                        </button>
                        <button 
                          className={styles.characterPlusButton}
                          onClick={(e) => handleAddCharacterToScenario(character.id, e)}
                          aria-label="Add character to scenario"
                        >
                          <GrUserAdd size={16} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {/* Add mode content */}
          {isAddMode && (
            <div className={`${styles.addModeContent} ${isAddingCharacter ? styles.fadeOut : ''}`}>
              <div className={styles.searchResultsContainer}>
                {isSearching && (
                  <div className={styles.loadingOverlay}>
                    <div className={styles.loadingSpinner}></div>
                  </div>
                )}
                {searchResults.length > 0 ? (
                  <div className={`${styles.searchResultsGrid} ${isSearching ? styles.searching : ''}`}>
                    {searchResults.map((character) => (
                      <WorldCharacterCard
                        key={character.id}
                        character={character}
                        onAddClick={handleSelectCharacter}
                        isSelected={selectedCharacter?.id === character.id}
                        isAddingCharacter={isAddingCharacter}
                        isLoading={loadingCharacterIds.includes(character.id)}
                      />
                    ))}
                  </div>
                ) : !isSearching && searchQuery.trim().length > 0 ? (
                  <div className={styles.searchResultsMessage}>
                    No characters found matching "{searchQuery}"
                  </div>
                ) : !isSearching ? (
                  <div className={styles.searchResultsMessage}>
                    Search for characters to add to your world
                  </div>
                ) : null}
              </div>
            </div>
          )}
          
          {children}
        </div>
      </div>
    </>
  );
}

export default SideDrawer; 